import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getBestyrelse, FALLBACK_BESTYRELSE } from '../lib/cms';

export default function OmOs() {
  const [bestyrelse, setBestyrelse] = useState(FALLBACK_BESTYRELSE);

  useEffect(() => {
    getBestyrelse().then((data) => {
      if (data && data.length > 0) {
        setBestyrelse(data);
      }
    });
  }, []);

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Om Foreningen ØrBike - Bestyrelse & Historie</title>
        <meta
          name="description"
          content="Foreningen ØrBike (CVR: 45950433) – Læs om foreningens bestyrelse (Team ØrBike), vedtægter, revision og historien bag Danmarks vildeste indoor bike fest."
        />
        <meta property="og:title" content="Om Foreningen ØrBike - Bestyrelse & Historie" />
        <meta property="og:description" content="Læs historien om Foreningen ØrBike, mød bestyrelsen og læs vores officielle vedtægter." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#141224] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            Historien bag Danmarks vildeste bike fest
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Om Foreningen ØrBike
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Sammen skaber vi smil, ét tråd ad gangen
          </p>
        </div>
      </section>

      {/* Indhold */}
      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Grundlagt i 2014 */}
        <div className="bg-[#12141f] border border-pink-500/30 rounded-3xl p-8 sm:p-12 space-y-6 shadow-xl">
          <h2 className="text-2xl sm:text-4xl font-black text-white font-display uppercase tracking-tight">
            Grundlagt i 2014 – drevet af frivillige kræfter
          </h2>
          <p className="text-slate-300 text-base leading-relaxed">
            ØrBike blev grundlagt i <strong>2014</strong> i samarbejde med <strong>Ørbæk Midtpunkt</strong>, hvor det første event blev afholdt.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            Tanken var at skabe noget helt særligt – et event, hvor <strong>motion, fællesskab og mening</strong> smelter sammen, og hvor deltagerne cykler for livet og for hinanden.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            Siden da har ØrBike hvert år – den <strong>anden lørdag i november</strong> – forvandlet Ørbæk Midtpunkt til et mekka af <strong>musik, energi og fællesskab</strong>.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            Med <strong>over 230 cykler og mere end 1.000 deltagere</strong> er ØrBike i dag et af Danmarks største indoor bike events, og hvert år samles både nye og erfarne ryttere for at opleve den helt særlige ØrBike-stemning.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            På scenen står nogle af <strong>Danmarks dygtigste og mest inspirerende instruktører</strong>, og gennem 10 timer fyldt med <strong>puls, musik og motivation</strong> bliver der skabt en unik oplevelse, hvor smilene vokser, efterhånden som benene arbejder.
          </p>
        </div>

        {/* Etablering af Foreningen i 2024 */}
        <div className="bg-gradient-to-b from-[#181a29] to-[#0f111e] border border-purple-500/30 rounded-3xl p-8 sm:p-12 space-y-6 shadow-xl">
          <h3 className="text-2xl sm:text-3xl font-black text-white font-display uppercase tracking-tight">
            En selvstændig forening med hjerteformål
          </h3>
          <p className="text-slate-300 text-base leading-relaxed">
            Fra begyndelsen har ØrBike haft et klart hjerteformål – at <strong>give tilbage</strong>.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            Gennem årene har eventet støttet lokale foreninger og velgørende formål, blandt andet gennem aflønning af frivillige og donationer til børn med kritisk sygdom.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            I <strong>2024</strong> blev der etableret en ny forening til fordel for at drive ØrBike videre efter Ørbæk Midtpunkt. Foreningen ØrBike står som en <strong>selvstændig forening</strong>, drevet af frivillige kræfter med base i Ørbæk.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            Eventet afholdes fortsat i <strong>Ørbæk Midtpunkt</strong>, hvor traditionen og fællesskabet lever videre – nu med endnu stærkere rødder og et klart formål.
          </p>
          <p className="text-slate-300 text-base leading-relaxed">
            En stor del af ØrBikes overskud går hvert år til et <strong>velgørende formål</strong>. Foreningen ØrBike donerer et så stort beløb som muligt og gerne minimum 70% af overskuddet, til fordel for den i året valgte sag. Når du deltager i ØrBike, er du derfor med til at skabe en uforglemmelig dag på cyklen – og samtidig gøre en forskel for nogle udsatte fx pga. sygdom eller andet.
          </p>
        </div>

        {/* Bestyrelsen aka Team ØrBike */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
              Ledelse & Frivillige Kræfter
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase tracking-tight mt-1">
              Bestyrelsen aka Team ØrBike
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Mød bestyrelsen bag Foreningen ØrBike, der med hjerte og engagement står i spidsen for eventet.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bestyrelse.map((medlem, i) => (
              <div
                key={i}
                className="p-6 rounded-3xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-all flex flex-col justify-between text-center relative group shadow-lg"
              >
                <div>
                  {/* Portræt med "Fotograf på vej" skilt */}
                  <div className="relative mx-auto w-32 h-32 rounded-2xl overflow-hidden bg-gradient-to-br from-purple-900/40 via-slate-800 to-pink-900/40 border border-pink-500/30 flex flex-col items-center justify-center p-3 mb-4 shadow-inner group-hover:scale-105 transition-transform duration-300">
                    <div className="w-12 h-12 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center text-xl mb-1 border border-pink-500/30">
                      <i className="fa-solid fa-user" aria-hidden="true"></i>
                    </div>
                    {/* Fotografen er på vej skilt */}
                    <div className="absolute inset-x-2 bottom-2 py-1 px-2 rounded-lg bg-pink-500/90 text-white text-[10px] font-bold uppercase tracking-wider shadow-md backdrop-blur-sm flex items-center justify-center gap-1">
                      <i className="fa-solid fa-camera text-[9px]" aria-hidden="true"></i>
                      <span>Fotograf på vej</span>
                    </div>
                  </div>

                  <h4 className="text-lg font-bold text-white font-display uppercase">
                    {medlem.navn}
                  </h4>
                  <p className="text-xs text-pink-400 font-medium mt-1 leading-snug">
                    {medlem.rolle}
                  </p>
                </div>

                <div className="pt-5 border-t border-slate-800/80 mt-4 space-y-2">
                  <a
                    href={`mailto:${medlem.email}`}
                    className="text-xs text-slate-300 hover:text-pink-400 transition-colors flex items-center justify-center gap-1.5 truncate"
                    title={medlem.email}
                  >
                    <i className="fa-solid fa-envelope text-pink-500 shrink-0" aria-hidden="true"></i>
                    <span className="truncate">{medlem.email}</span>
                  </a>
                  <a
                    href={medlem.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <i className="fa-brands fa-facebook text-sm" aria-hidden="true"></i>
                    <span>Find på Facebook</span>
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Revision og Frivillige boks */}
          <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-slate-900 to-purple-950/30 border border-pink-500/30 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="space-y-1.5">
              <span className="text-xs font-mono font-bold uppercase text-pink-400">Økonomisk kontrol & fællesskab</span>
              <h4 className="text-xl font-bold text-white font-display uppercase">
                Revision & Frivillige Kræfter
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed">
                Revision foretages af <strong>Team Rynkeby Fondens økonomi afdeling</strong>.
              </p>
              <p className="text-xs text-slate-400">
                Intet ØrBike uden fantastiske frivillige i år fra <strong>Team Rynkeby Ringe</strong> og <strong>Team Rynkeby Cycling Club</strong>.
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              <span className="px-4 py-2 rounded-xl bg-pink-500/10 border border-pink-500/30 text-pink-300 text-xs font-bold uppercase">
                Team Rynkeby Ringe
              </span>
              <span className="px-4 py-2 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-bold uppercase">
                TR Cycling Club
              </span>
            </div>
          </div>
        </div>

        {/* Vedtægter Sektion */}
        <div className="p-8 sm:p-10 rounded-3xl bg-[#12141f] border border-purple-500/30 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
          <div className="flex items-start gap-5 text-left">
            <div className="w-14 h-14 rounded-2xl bg-purple-500/20 border border-purple-500/30 text-purple-400 flex items-center justify-center text-2xl shrink-0">
              <i className="fa-solid fa-file-shield" aria-hidden="true"></i>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-mono font-bold uppercase text-purple-400">Officielle dokumenter</span>
              <h3 className="text-2xl font-bold text-white font-display uppercase">
                Vedtægter for Foreningen ØrBike
              </h3>
              <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
                Her kan du læse de officielle vedtægter for Foreningen ØrBike. Vedtægterne fastsætter foreningens non-profit formål, ledelse og velgørende forankring.
              </p>
              <div className="text-xs text-slate-500 pt-1">
                Foreningen ØrBike • CVR: 45950433 • Hjemsted: Svendborg Kommune
              </div>
            </div>
          </div>

          <div className="shrink-0 w-full md:w-auto">
            <a
              href="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/foreningen-oerbike-vedtaegter-09-2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold text-sm uppercase tracking-wider shadow-lg transition-transform hover:scale-105"
            >
              <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
              <span>Åbn Vedtægter (PDF)</span>
            </a>
          </div>
        </div>

        {/* Faktaboks */}
        <div className="p-8 rounded-3xl bg-slate-900/90 border border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <h4 className="font-bold text-white font-display text-xl uppercase mb-3">Foreningsfakta</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><strong>Forening:</strong> Foreningen ØrBike</li>
              <li><strong>CVR:</strong> <span className="whitespace-nowrap font-mono text-pink-400">45950433</span></li>
              <li><strong>Eventarrangører:</strong> Helle Holst Hansen og Jonas Reinholdt Albjerg</li>
              <li><strong>Adresse:</strong> Degnemosen 12, Tåsinge, 5700 Svendborg</li>
              <li><strong>Email:</strong> <a href="mailto:info@orbike.dk" className="text-pink-400 underline">info@orbike.dk</a></li>
              <li><strong>Telefon:</strong> <a href="tel:4530936272" className="text-pink-400 underline">+45 30 93 62 72</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-white font-display text-xl uppercase mb-3">Eventsted</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><strong>Lokation:</strong> Ørbæk Midtpunkt</li>
              <li><strong>Adresse:</strong> Langemosevej 5, 5853 Ørbæk</li>
              <li><strong>Kapacitet:</strong> 230+ cykler, 1.000+ deltagere</li>
              <li><strong>Tidspunkt:</strong> Anden lørdag i november</li>
              <li><strong>Revision:</strong> Team Rynkeby Fondens økonomi afdeling</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-4">
          <Link
            to="/eventet"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
          >
            <span>Læs mere om ØrBike Eventet</span>
            <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}
